import React, { useEffect } from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";
import {
  ConciergeHomeStartersState,
  StartersMode,
  ElementStyle,
  CardLayoutDirection,
} from "./types";
import { DEFAULT_STARTER_ICONS } from "../../../../assets/starter-icons/defaultIcons";

/**
 * Load a Google Font stylesheet into document.head.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: ConciergeHomeStartersState = {
  // Pill styling — matches Figma 3468:4971 (fully rounded pill)
  pillPaddingX: 24,
  pillPaddingY: 12,
  pillBorderRadius: 100,
  pillBorderWidth: 1,
  pillBorderColor: themeColor('borderColor'),
  pillBgColor: themeColor('background'),
  pillBgColorHover: themeColor('background'),
  pillTextColor: themeColor('textPrimary'),
  pillFontSize: 14,
  pillFontWeight: 'medium',
  pillShadowEnabled: true,
  pillShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.05)',
  pillGap: 8,
  fontFamily: 'inherit',

  // Card styling
  cardWidth: 183,
  cardHeight: 124,
  cardPadding: 16,
  cardBorderRadius: 12,
  cardBorderWidth: 0,
  cardBorderColor: themeColor('borderColor'),
  cardBgColor: themeColor('background'),
  cardBgColorHover: themeColor('background'),
  cardTextColor: themeColor('textPrimary'),
  cardFontSize: 14,
  cardGap: 12,
  cardIconSize: 32,

  // Default icons (will be replaced by bundled SVGs)
  starterIcon1: '',
  starterIcon2: '',
  starterIcon3: '',
  starterIcon4: '',

  // Container
  containerMaxWidth: 768,
  containerMarginTop: 16,

  // Mobile overrides — matches Figma 3468:4971 (padding 12×16, 14/22 Inter Medium)
  mobile_pillFontSize: 14,
  mobile_pillPaddingX: 16,
  mobile_pillPaddingY: 12,
  mobile_cardGap: 8,
};

const editFields: EditField[] = [
  // Pill styling
  {
    key: "pillPaddingX",
    label: "Pill Padding X",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
    responsive: true,
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillPaddingY",
    label: "Pill Padding Y",
    type: "number",
    min: 6,
    max: 20,
    step: 2,
    responsive: true,
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillBorderRadius",
    label: "Pill Border Radius",
    type: "number",
    min: 0,
    max: 100,
    step: 2,
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillBorderWidth",
    label: "Pill Border Width",
    type: "number",
    min: 0,
    max: 3,
    step: 1,
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillBorderColor",
    label: "Pill Border Color",
    type: "color",
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillBgColor",
    label: "Pill Background",
    type: "color",
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillBgColorHover",
    label: "Pill Bg (Hover)",
    type: "color",
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillTextColor",
    label: "Pill Text Color",
    type: "color",
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillFontSize",
    label: "Pill Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
    group: "Pill Styling",
    responsive: true,
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillFontWeight",
    label: "Pill Font Weight",
    type: "options",
    options: [
      { label: "Normal", value: "normal" },
      { label: "Medium", value: "medium" },
      { label: "Semibold", value: "semibold" },
      { label: "Bold", value: "bold" },
    ],
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillShadowEnabled",
    label: "Shadow",
    type: "toggle",
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillShadow",
    label: "Shadow Value (CSS)",
    type: "text",
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },
  {
    key: "pillGap",
    label: "Pill Gap",
    type: "number",
    min: 4,
    max: 16,
    step: 2,
    group: "Pill Styling",
    condition: { field: "_startersMode", value: "pills", operator: "includes" },
  },

  // Card styling
  {
    key: "cardWidth",
    label: "Card Width",
    type: "number",
    min: 120,
    max: 240,
    step: 10,
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardHeight",
    label: "Card Height",
    type: "number",
    min: 80,
    max: 180,
    step: 10,
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardPadding",
    label: "Card Padding",
    type: "number",
    min: 8,
    max: 24,
    step: 4,
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardBorderRadius",
    label: "Card Border Radius",
    type: "number",
    min: 0,
    max: 24,
    step: 2,
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardBgColor",
    label: "Card Background",
    type: "color",
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardBgColorHover",
    label: "Card Bg (Hover)",
    type: "color",
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardTextColor",
    label: "Card Text Color",
    type: "color",
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardFontSize",
    label: "Card Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardGap",
    label: "Card Gap",
    type: "number",
    min: 4,
    max: 24,
    step: 4,
    group: "Card Styling",
    responsive: true,
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },
  {
    key: "cardIconSize",
    label: "Card Icon Size",
    type: "number",
    min: 16,
    max: 48,
    step: 4,
    group: "Card Styling",
    condition: { field: "_startersMode", value: "cards", operator: "includes" },
  },

  // Icons
  {
    key: "starterIcon1",
    label: "Starter 1 Icon",
    type: "icon",
    group: "Icons",
  },
  {
    key: "starterIcon2",
    label: "Starter 2 Icon",
    type: "icon",
    group: "Icons",
  },
  {
    key: "starterIcon3",
    label: "Starter 3 Icon",
    type: "icon",
    group: "Icons",
  },
  {
    key: "starterIcon4",
    label: "Starter 4 Icon",
    type: "icon",
    group: "Icons",
  },

  // Typography
  {
    key: "fontFamily",
    label: "Font Family",
    type: "font",
    group: "Typography",
  },

  // Container
  {
    key: "containerMaxWidth",
    label: "Max Width",
    type: "number",
    min: 300,
    max: 900,
    step: 50,
    group: "Container",
  },
  {
    key: "containerMarginTop",
    label: "Margin Top",
    type: "number",
    min: 0,
    max: 48,
    step: 4,
    group: "Container",
  },
];

interface ConciergeHomeStartersWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeStartersState>;
  mode: StartersMode;
  elementStyle?: ElementStyle;
  cardLayoutDirection?: CardLayoutDirection;
  maxStarters?: number;
  queries: string[];
  onSearch: (query: string) => void;
}

export const ConciergeHomeStartersWidget: React.FC<
  ConciergeHomeStartersWidgetProps
> = ({
  widgetId = "concierge-home-starters",
  displayName = "Conversation Starters",
  initialState = {},
  mode,
  elementStyle = "default",
  cardLayoutDirection = "stacked",
  maxStarters = 4,
  queries,
  onSearch,
}) => {
  const {
    state: rawState,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    resetField,
    savedKeys,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<
    ConciergeHomeStartersState & { _startersMode?: string }
  >(
    widgetId,
    { ...defaultState, ...initialState, _startersMode: mode },
    editFields,
  );

  // Inject _startersMode for condition evaluation in edit panel
  const state = { ...rawState, _startersMode: mode };

  // Resolve font family
  const fontKey = state.fontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading || mode === "hidden" || queries.length === 0) {
    return <></>;
  }

  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  const uniqueFontClass = `starters-font-${widgetId || "default"}`;

  const fontStyleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ""
    }
    ${
      fontFamilyCss !== "inherit"
        ? `.${uniqueFontClass} { font-family: ${fontFamilyCss} !important; }`
        : ""
    }
  `.trim();

  const visibleQueries = queries.slice(0, maxStarters);
  const iconKeys = [
    state.starterIcon1,
    state.starterIcon2,
    state.starterIcon3,
    state.starterIcon4,
  ];

  /**
   * Resolve the icon URL for a starter slot.
   * Priority: theme icon key > default bundled SVG.
   */
  const getIconUrl = (index: number): string | null => {
    const key = iconKeys[index];
    // If a theme icon key is set and exists in theme icons, use it
    if (key && themeIcons[key]) {
      return themeIcons[key].url;
    }
    // Otherwise use the bundled default SVG (data URI)
    return DEFAULT_STARTER_ICONS[index] || null;
  };

  // Resolve element style overrides for starters
  const getElementBg = () => {
    if (elementStyle === "grey-fill" || elementStyle === "grey-fill-stroke") {
      return "#f6f8fa";
    }
    return undefined;
  };
  const getElementBorder = () => {
    if (elementStyle === "grey-fill-stroke") {
      return "1px solid #e5e7eb";
    }
    if (elementStyle === "grey-fill") {
      return "none";
    }
    return undefined;
  };

  const fontWeight = (fw: string) =>
    fw === "normal"
      ? 400
      : fw === "medium"
        ? 500
        : fw === "semibold"
          ? 600
          : 700;

  const renderPill = (query: string, index: number) => {
    const showIcon = mode === "pills-icons";
    const iconUrl = getIconUrl(index);

    const bgOverride = getElementBg();
    const borderOverride = getElementBorder();

    return (
      <button
        key={index}
        className="concierge-home-starters-pill"
        onClick={() => onSearch(query)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: `${state.pillPaddingY}px ${state.pillPaddingX}px`,
          borderRadius: state.pillBorderRadius,
          border:
            borderOverride ??
            `${state.pillBorderWidth}px solid ${resolveColor(state.pillBorderColor)}`,
          backgroundColor: bgOverride ?? resolveColor(state.pillBgColor),
          color: resolveColor(state.pillTextColor),
          fontSize: state.pillFontSize,
          lineHeight: "22px",
          fontWeight: fontWeight(state.pillFontWeight),
          cursor: "pointer",
          textAlign: "left",
          transition: "all 0.2s ease",
          whiteSpace: "nowrap",
          boxShadow: state.pillShadowEnabled ? state.pillShadow : "none",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            resolveColor(state.pillBgColorHover) ||
            bgOverride ||
            resolveColor(state.pillBgColor);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor =
            bgOverride ?? resolveColor(state.pillBgColor);
        }}
      >
        {showIcon && iconUrl && (
          <img
            src={iconUrl}
            alt=""
            style={{
              width: 24,
              height: 24,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        )}
        {query}
      </button>
    );
  };

  const renderCard = (query: string, index: number) => {
    const iconUrl = getIconUrl(index);

    const bgOverride = getElementBg();
    const borderOverride = getElementBorder();
    const isInline = cardLayoutDirection === "inline";

    return (
      <button
        key={index}
        onClick={() => onSearch(query)}
        style={{
          display: "flex",
          flexDirection: isInline ? "row" : "column",
          alignItems: isInline ? "center" : undefined,
          gap: isInline ? "16px" : "12px",
          padding: state.cardPadding,
          borderRadius: state.cardBorderRadius,
          border:
            borderOverride ??
            (state.cardBorderWidth > 0
              ? `${state.cardBorderWidth}px solid ${resolveColor(state.cardBorderColor)}`
              : "none"),
          backgroundColor: bgOverride ?? resolveColor(state.cardBgColor),
          color: resolveColor(state.cardTextColor),
          fontSize: state.cardFontSize,
          cursor: "pointer",
          textAlign: "left",
          transition: "all 0.2s ease",
          width: mode === "cards-grid" ? "100%" : state.cardWidth,
          minHeight: isInline ? undefined : state.cardHeight,
          flexShrink: 0,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            resolveColor(state.cardBgColorHover) ||
            bgOverride ||
            resolveColor(state.cardBgColor);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor =
            bgOverride ?? resolveColor(state.cardBgColor);
        }}
      >
        {iconUrl && (
          <img
            src={iconUrl}
            alt=""
            style={{
              width: state.cardIconSize,
              height: state.cardIconSize,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        )}
        <span style={{ lineHeight: 1.4 }}>{query}</span>
      </button>
    );
  };

  const renderPillIconListItem = (query: string, index: number) => {
    const iconUrl = getIconUrl(index);
    const bgOverride = getElementBg();
    const borderOverride = getElementBorder();

    return (
      <button
        key={index}
        className="concierge-home-starters-pill"
        onClick={() => onSearch(query)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          width: "100%",
          padding: `${state.pillPaddingY}px ${state.pillPaddingX}px`,
          borderRadius:
            state.pillBorderRadius > 20 ? 10 : state.pillBorderRadius,
          border:
            borderOverride ??
            `${state.pillBorderWidth}px solid ${resolveColor(state.pillBorderColor)}`,
          backgroundColor: bgOverride ?? resolveColor(state.pillBgColor),
          color: resolveColor(state.pillTextColor),
          fontSize: state.pillFontSize,
          fontWeight: fontWeight(state.pillFontWeight),
          cursor: "pointer",
          textAlign: "left",
          transition: "all 0.2s ease",
          boxSizing: "border-box",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            resolveColor(state.pillBgColorHover) ||
            bgOverride ||
            resolveColor(state.pillBgColor);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor =
            bgOverride ?? resolveColor(state.pillBgColor);
        }}
      >
        {iconUrl && (
          <img
            src={iconUrl}
            alt=""
            style={{
              width: 16,
              height: 16,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        )}
        {query}
      </button>
    );
  };

  const renderStarters = () => {
    if (mode === "pills" || mode === "pills-icons") {
      const shadowPad = state.pillShadowEnabled ? 16 : 0;
      return (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: state.pillGap,
            justifyContent: "center",
            maxWidth: state.containerMaxWidth,
            margin: `0 auto -${shadowPad}px`,
            padding: `0 0 ${shadowPad}px`,
          }}
          className="concierge-home-starters-pills"
        >
          {visibleQueries.map((q, i) => renderPill(q, i))}
        </div>
      );
    }

    if (mode === "pills-icons-list") {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: state.pillGap,
            maxWidth: state.containerMaxWidth,
            margin: "0 auto",
            width: "100%",
          }}
          className="concierge-home-starters-pills-list"
        >
          {visibleQueries.map((q, i) => renderPillIconListItem(q, i))}
        </div>
      );
    }

    if (mode === "cards-row") {
      return (
        <div
          style={{
            display: "flex",
            gap: state.cardGap,
            justifyContent: "center",
            maxWidth: state.containerMaxWidth,
            margin: "0 auto",
            flexWrap: "wrap",
          }}
          className="concierge-home-starters-cards-row"
        >
          {visibleQueries.map((q, i) => renderCard(q, i))}
        </div>
      );
    }

    if (mode === "cards-grid") {
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: state.cardGap,
            maxWidth: state.containerMaxWidth,
            margin: "0 auto",
          }}
          className="concierge-home-starters-cards-grid"
        >
          {visibleQueries.map((q, i) => renderCard(q, i))}
        </div>
      );
    }

    return null;
  };

  const content = (
    <div
      className={fontFamilyCss !== 'inherit' ? uniqueFontClass : undefined}
      style={{ marginTop: state.containerMarginTop, width: '100%' }}
    >
      <style>{`
        ${fontStyleCss ? fontStyleCss : ''}
        /* Responsive: pills become a horizontal carousel on TOUCH mobile.
           Gated on \`pointer: coarse\` as well as width, because a desktop
           browser zoomed to 200% also reports a sub-768px viewport — and there
           the carousel is a WCAG 1.4.10 (reflow) failure: the row stops
           wrapping and the middle starter gets cut off at the viewport edge
           with no obvious way to reach it. A zoomed desktop keeps the wrapping
           layout; phones are untouched. */
        @media (max-width: 767px) and (pointer: coarse) {
          .concierge-home-starters-pills {
            flex-wrap: nowrap !important;
            justify-content: flex-start !important;
            overflow-x: auto !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            padding-bottom: 4px;
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .concierge-home-starters-pills::-webkit-scrollbar {
            display: none;
          }
        }
        /* Zoomed / narrow desktop: the row still wraps, so each starter must be
           allowed to break mid-label and stay inside the viewport. Without this
           the button's own \`white-space: nowrap\` keeps it one long line that
           overflows. Excluded from touch mobile, where the carousel above
           intends single-line pills. */
        @media (max-width: 767px) and (pointer: fine) {
          .concierge-home-starters-pill {
            white-space: normal !important;
            max-width: 100% !important;
          }
        }
        @media (max-width: 767px) {
          .concierge-home-starters-pills {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
          .concierge-home-starters-pill {
            padding: ${(state as any).mobile_pillPaddingY ?? state.pillPaddingY}px ${(state as any).mobile_pillPaddingX ?? state.pillPaddingX}px !important;
            font-size: ${(state as any).mobile_pillFontSize ?? state.pillFontSize}px !important;
            line-height: 22px !important;
          }
          /* Vertical pill list on mobile */
          .concierge-home-starters-pills-list {
            padding: 0 16px !important;
            box-sizing: border-box !important;
          }
          /* Cards row becomes 2-column grid on mobile */
          .concierge-home-starters-cards-row {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: ${state.mobile_cardGap}px !important;
            padding: 0 16px !important;
            box-sizing: border-box !important;
          }
          .concierge-home-starters-cards-row > button {
            width: 100% !important;
            min-height: 88px !important;
            font-size: 12px !important;
            border-radius: 10px !important;
            padding: 12px 15px !important;
            gap: 8px !important;
          }
          .concierge-home-starters-cards-row > button img {
            width: 16px !important;
            height: 16px !important;
          }
          /* Cards grid stays 2-column on mobile with compact sizing */
          .concierge-home-starters-cards-grid {
            gap: ${state.mobile_cardGap}px !important;
            padding: 0 16px !important;
            box-sizing: border-box !important;
          }
          .concierge-home-starters-cards-grid > button {
            min-height: 88px !important;
            font-size: 12px !important;
            border-radius: 10px !important;
            padding: 12px 15px !important;
            gap: 8px !important;
          }
          .concierge-home-starters-cards-grid > button img {
            width: 16px !important;
            height: 16px !important;
          }
        }
      `}</style>
      {renderStarters()}
    </div>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
    >
      {content}
    </WidgetWrapper>
  );
};
